import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Tabs,
  Table,
  Button,
  Row,
  Col,
  Form,
  message,
  Tag,
  Alert,
  Avatar,
  Space,
} from 'antd';
import {
  BarChartOutlined,
  TeamOutlined,
  RobotOutlined,
  ReloadOutlined,
  PhoneOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import PersianDatePicker from '../../components/PersianDatePicker';
import { callCenterReportsApi } from '../../utils/api';
import { getLast30DaysRange, toGregorian } from '../../utils/dateUtils';
import { parseDateRangeChange, getDateRangePickerValue } from '../reports/reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { useListReturnState } from '../../hooks/useListReturnState';

const formatSeconds = (seconds) => {
  if (seconds == null) return '—';
  const s = Math.round(Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const toApiDate = (jalaliStr) => {
  if (!jalaliStr) return null;
  const normalized = String(jalaliStr).replace(/-/g, '/');
  return toGregorian(normalized, 'YYYY-MM-DD');
};

const AI_STATUS_META = {
  none: { label: '—', color: 'default' },
  pending: { label: 'در صف', color: 'processing' },
  completed: { label: 'انجام شده', color: 'success' },
  failed: { label: 'خطا', color: 'error' },
};

const CallCenterReports = () => {
  const defaultRange = useMemo(() => getLast30DaysRange('jYYYY/jMM/jDD'), []);

  const [activeTab, setActiveTab] = useState('agents');
  const [dateRange, setDateRange] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });

  const [agentsLoading, setAgentsLoading] = useState(false);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [agentsData, setAgentsData] = useState(null);
  const [qualityData, setQualityData] = useState(null);

  const captureCurrentListState = useCallback(
    () => ({
      activeTab,
      dateRange,
    }),
    [activeTab, dateRange],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.activeTab) setActiveTab(restored.activeTab);
    if (restored.dateRange) setDateRange(restored.dateRange);
  });

  const apiFrom = toApiDate(dateRange.startDate);
  const apiTo = toApiDate(dateRange.endDate);

  const loadAgentReport = useCallback(async () => {
    if (!apiFrom || !apiTo) return;
    setAgentsLoading(true);
    try {
      const res = await callCenterReportsApi.getAgentPerformance({ from: apiFrom, to: apiTo });
      setAgentsData(res?.data ?? null);
    } catch (error) {
      console.error('خطا در گزارش اپراتورها:', error);
      message.error(error?.message || 'خطا در دریافت گزارش عملکرد اپراتورها');
      setAgentsData(null);
    } finally {
      setAgentsLoading(false);
    }
  }, [apiFrom, apiTo]);

  const loadQualityReport = useCallback(async () => {
    if (!apiFrom || !apiTo) return;
    setQualityLoading(true);
    try {
      const res = await callCenterReportsApi.getQualityComparison({ from: apiFrom, to: apiTo });
      setQualityData(res?.data ?? null);
    } catch (error) {
      console.error('خطا در گزارش کیفیت:', error);
      message.error(error?.message || 'خطا در دریافت گزارش مقایسه کیفیت');
      setQualityData(null);
    } finally {
      setQualityLoading(false);
    }
  }, [apiFrom, apiTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'agents') loadAgentReport();
      else loadQualityReport();
    }, 350);
    return () => clearTimeout(timer);
  }, [activeTab, dateRange, loadAgentReport, loadQualityReport]);

  const agentRows = agentsData?.agents ?? [];

  const chartData = useMemo(
    () =>
      agentRows.map((row) => ({
        name: row.agent?.user?.name || row.agent?.extension || '—',
        answered: row.answered_total ?? 0,
        missed: row.missed_total ?? 0,
      })),
    [agentRows],
  );

  const agentColumns = [
    {
      title: 'اپراتور',
      key: 'agent',
      fixed: 'left',
      width: 180,
      render: (_, row) => {
        const user = row.agent?.user;
        const name = user?.name || '—';
        return (
          <Space>
            <Avatar size={36} className="bg-teal-500 text-white shrink-0">
              {name.charAt(0)}
            </Avatar>
            <div>
              <div className="custom-font font-medium">{name}</div>
              <div className="custom-font text-xs text-gray-500" dir="ltr">
                {row.agent?.extension || '—'}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'ورودی',
      dataIndex: 'incoming_total',
      key: 'incoming_total',
      width: 80,
      render: (v) => <span className="custom-font" dir="ltr">{v ?? 0}</span>,
    },
    {
      title: 'خروجی',
      dataIndex: 'outgoing_total',
      key: 'outgoing_total',
      width: 80,
      render: (v) => <span className="custom-font" dir="ltr">{v ?? 0}</span>,
    },
    {
      title: 'پاسخ‌داده',
      dataIndex: 'answered_total',
      key: 'answered_total',
      width: 90,
      render: (v) => <Tag color="green" className="custom-font">{v ?? 0}</Tag>,
    },
    {
      title: 'ازدست‌رفته',
      dataIndex: 'missed_total',
      key: 'missed_total',
      width: 90,
      render: (v) => <Tag color="orange" className="custom-font">{v ?? 0}</Tag>,
    },
    {
      title: 'میانگین مکالمه',
      dataIndex: 'avg_talk_seconds',
      key: 'avg_talk_seconds',
      width: 110,
      render: (v) => <span className="custom-font" dir="ltr">{formatSeconds(v)}</span>,
    },
    {
      title: 'میانگین انتظار',
      dataIndex: 'avg_wait_seconds',
      key: 'avg_wait_seconds',
      width: 110,
      render: (v) => <span className="custom-font" dir="ltr">{formatSeconds(v)}</span>,
    },
    {
      title: 'کل مکالمه',
      dataIndex: 'total_talk_seconds',
      key: 'total_talk_seconds',
      width: 100,
      render: (v) => <span className="custom-font" dir="ltr">{formatSeconds(v)}</span>,
    },
  ];

  const qualityColumns = [
    {
      title: 'شماره',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (phone) => (
        <span className="custom-font" dir="ltr">
          <PhoneOutlined className="text-gray-400 ml-1" />
          {phone || '—'}
        </span>
      ),
    },
    {
      title: 'امتیاز اپراتور',
      dataIndex: 'operator_score',
      key: 'operator_score',
      width: 120,
      render: (score) =>
        score != null ? (
          <Tag color={score >= 80 ? 'green' : score >= 60 ? 'gold' : 'red'} className="custom-font text-base" title="امتیاز ۰ تا ۱۰۰ هوش مصنوعی به عملکرد اپراتور">
            <span dir="ltr">{score} / 100</span>
          </Tag>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      title: 'FCR',
      dataIndex: 'fcr',
      key: 'fcr',
      width: 100,
      render: (fcr) =>
        fcr == null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <Tag color={fcr ? 'success' : 'error'} className="custom-font" title="حل مشکل در همان تماس اول">
            {fcr ? 'موفق' : 'ناموفق'}
          </Tag>
        ),
    },
    {
      title: 'وضعیت AI',
      dataIndex: 'ai_status',
      key: 'ai_status',
      width: 120,
      render: (status) => {
        const meta = AI_STATUS_META[status] || AI_STATUS_META.none;
        if (!status || status === 'none') return <span className="text-gray-400">—</span>;
        return <Tag color={meta.color} className="custom-font">{meta.label}</Tag>;
      },
    },
    {
      title: 'خلاصه AI',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      render: (text) => (
        <span className="custom-font text-sm text-gray-600 dark:text-gray-400">
          {text || '—'}
        </span>
      ),
    },
    {
      title: 'مدت',
      dataIndex: 'billsec',
      key: 'billsec',
      width: 90,
      render: (v) => <span className="custom-font" dir="ltr">{formatSeconds(v)}</span>,
    },
    {
      title: 'زمان',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 150,
      render: (dt) => <span className="custom-font text-sm" dir="ltr">{dt || '—'}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, row) =>
        row.id ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigateToDetail(`/call-center/calls/${row.id}`)}
          />
        ) : null,
    },
  ];

  const handleRefresh = () => {
    if (activeTab === 'agents') loadAgentReport();
    else loadQualityReport();
  };

  const dateFilter = (
    <Card
      size="small"
      className="mb-4 border border-gray-100 dark:border-gray-700"
      title={<span className="custom-font text-sm">بازه زمانی</span>}
    >
      <Form layout="vertical" className="custom-font">
        <Row gutter={16} align="middle">
          <Col xs={24} md={12} lg={10}>
            <PersianDatePicker
              range
              value={getDateRangePickerValue(dateRange.startDate, dateRange.endDate)}
              onChange={(dates) => {
                const { startDate, endDate } = parseDateRangeChange(dates);
                setDateRange({ startDate, endDate });
              }}
              placeholder="انتخاب بازه تاریخ"
              style={{ width: '100%', fontFamily: 'CustomFont' }}
              allowClear={false}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={agentsLoading || qualityLoading}>
              بروزرسانی
            </Button>
          </Col>
        </Row>
        {/* {agentsData?.from && agentsData?.to && activeTab === 'agents' && (
          <div className="custom-font text-xs text-gray-500 mt-2" dir="ltr">
            بازه گزارش: {agentsData.from} — {agentsData.to}
          </div>
        )} */}
      </Form>
    </Card>
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shadow-lg">
                <BarChartOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  گزارش‌های مرکز تماس
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  عملکرد اپراتورها و مقایسه کیفیت تماس
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {dateFilter}

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              className="custom-font"
              items={[
                {
                  key: 'agents',
                  label: (
                    <span className="custom-font">
                      <TeamOutlined className="ml-1" />
                      عملکرد اپراتورها
                    </span>
                  ),
                  children: agentsLoading && !agentRows.length ? (
                    <TableSkeleton rows={6} columns={7} />
                  ) : (
                    <div className="space-y-6">
                      {chartData.length > 0 && (
                        <Card size="small" title={<span className="custom-font">نمودار پاسخ / ازدست‌رفته</span>}>
                          <div className="h-64 w-full" dir="ltr">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="answered" name="پاسخ‌داده" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="missed" name="ازدست‌رفته" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </Card>
                      )}
                      <Table
                        rowKey={(r) => r.agent?.id ?? r.agent?.user_id}
                        columns={agentColumns}
                        dataSource={agentRows}
                        scroll={{ x: 900 }}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: 'داده‌ای برای این بازه یافت نشد' }}
                      />
                    </div>
                  ),
                },
                {
                  key: 'quality',
                  label: (
                    <span className="custom-font">
                      <RobotOutlined className="ml-1" />
                      مقایسه کیفیت
                    </span>
                  ),
                  children: qualityLoading && !qualityData?.items?.length ? (
                    <TableSkeleton rows={5} columns={6} />
                  ) : (
                    <div className="space-y-4">
                      <Alert
                        type="info"
                        showIcon
                        className="custom-font"
                        message="این دو شاخص معناهای متفاوتی دارند و با هم ترکیب نمی‌شوند"
                        description="امتیاز عملکرد اپراتور (از ۱۰۰) که هوش مصنوعی همراه با دلایل می‌دهد؛ و FCR نشان می‌دهد مشکل در همان تماس اول حل شده است یا نه."
                      />
                      <Table
                        rowKey="id"
                        columns={qualityColumns}
                        dataSource={qualityData?.items ?? []}
                        scroll={{ x: 1100 }}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: 'داده‌ای برای این بازه یافت نشد' }}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CallCenterReports;
